/**
 * DownloadAppBanner — promotes the native app install on the marketing
 * site (trybeup.com), to visitors who don't already have the app installed.
 * Live on both the App Store (iOS) and Google Play (Android). Hidden inside
 * the Capacitor app itself (we ARE the app there) and dismissible per device.
 *
 * Three display modes:
 *   - **iOS Safari (mobile web)**: full-width sticky bottom banner → App Store.
 *   - **Android web**: full-width sticky bottom banner → Google Play.
 *   - **Desktop**: smaller bottom-right pill listing BOTH stores (the visitor
 *     could have either phone).
 *
 * Apple's `apple-itunes-app` meta tag in index.html already triggers a
 * native iOS Safari banner at the very top of the page when supported.
 * This component is the complement for everything that meta tag misses
 * (older iOS, in-app browsers, desktop, Android, dismissed Safari banner).
 *
 * Dismissal lives in localStorage keyed by device — if the user closes
 * it once, we don't pester them again on this device for 30 days.
 */
import { useCachedMe } from "@/hooks/useCachedMe";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { closeButtonClass, CloseIcon } from "@/components/ui/close-button";
import { useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { X, Apple } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { APP_STORE_URL, GOOGLE_PLAY_URL, currentWebDevice } from "@/lib/storeLinks";
import { COMPLETION_EVENT, readOptInState } from "@/lib/notificationOptIn";
import { ACTIVE_TAB_EVENT, getActiveAppTab } from "@/lib/activeTab";

/** The official Apple logo silhouette in metallic silver (like apple.com),
 *  rather than the stylized apple glyph. */
function AppleLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 384 512" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="apple-silver" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#eceef0" />
          <stop offset="0.55" stopColor="#c2c7cb" />
          <stop offset="1" stopColor="#9aa0a5" />
        </linearGradient>
      </defs>
      <path fill="url(#apple-silver)" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

/** The real multi-color Google Play triangle (official brand colors), so it
 *  reads as Google Play — not a generic monochrome "play" glyph. */
function GooglePlayIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 256 283" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M119.553 134.916 1.06 259.061a32.14 32.14 0 0 0 47.062 19.071l133.327-75.934-61.896-67.282Z" fill="#EA4335" />
      <path d="m221.388 116.784-57.59-33.28-64.557 57.395 64.804 64.802 57.202-33.052c17.048-9.13 17.048-33.696 0-42.865Z" fill="#FBBC04" />
      <path d="M1.06 23.487A31.545 31.545 0 0 0 0 31.61v219.827c0 2.827.38 5.62 1.06 8.124l122.588-122.588L1.06 23.487Z" fill="#4285F4" />
      <path d="M120.436 141.523 179.66 82.298 48.302 7.19A32.36 32.36 0 0 0 32.145 2.995c-14.877 0-27.394 10.076-31.085 23.766l119.376 114.762Z" fill="#34A853" />
    </svg>
  );
}

/**
 * Public marketing / static routes where the banner shows to anyone.
 * The in-app dashboard, profile, messages, admin, etc. are deliberately
 * excluded. PublicProfile (`/profile/:id`) and other share-link routes are
 * excluded too because they tend to be opened from outside (Instagram,
 * WhatsApp, etc.) where the share context already implies the visitor knows
 * what TrybeUP is.
 *
 * `/` was removed 2026-10-08. Logged out, it's the Gateway — the ad landing
 * page — and the banner sat over its bottom row ("Sign in · Look around
 * first") 800ms after load. Installing from it also lost the ad's UTM and the
 * problem the visitor had just typed (neither crosses into the app). Signed-in
 * web users on `/` or `/app` get it instead, once they've done a first step —
 * see APP_PATHS below.
 */
const MARKETING_PATHS = new Set([
  "/landing",
  "/about",
  "/terms",
  "/privacy",
  "/faq",
  "/support",
  "/why",
  // /login and /reset-password were removed 2026-08-04: at phone width the
  // floating banner sat ON TOP of the signup form's "Begin Day 1" button
  // (found while browser-testing the new required-fields signup — the test's
  // taps landed on the banner instead of the submit). Someone mid-signup or
  // mid-password-reset is already committed; the banner earns nothing there
  // and can only get in the way of the most important button on the page.
]);

/** The signed-in app. The banner shows here only to a signed-in web user who
 *  has already completed a step (the same counter the reminders offer uses),
 *  so it's never the first thing a new account sees. Pilot users are left to
 *  the Pilot card's own install offer, which appears after their first Done
 *  — two prompts for one thing would be one too many. */
const APP_PATHS = new Set(["/", "/app"]);

/** Inside the signed-in app only the My Day tab: other tabs have their own
 *  fixed bottom bars (Workouts' action bar) the banner would cover, and on
 *  Android the PWA install prompt already sits there (review of 1.3.160). */
function onMyDayTab(search: string, appTab?: string | null): boolean {
  // MainApp's own tab when it has said (it strips ?tab= from the address);
  // the URL only as a fallback.
  const tab = appTab ?? new URLSearchParams(search).get("tab");
  return !tab || tab === "myday";
}

/** Pure: should the banner render on this path for this visitor? */
export function bannerAllowed(opts: {
  path: string;
  signedIn: boolean;
  completions: number;
  pilotMode: boolean;
  search?: string;
  /** MainApp's current tab (lib/activeTab), when it is mounted. */
  appTab?: string | null;
  android?: boolean;
}): boolean {
  if (MARKETING_PATHS.has(opts.path)) return true;
  if (APP_PATHS.has(opts.path)) {
    return opts.signedIn && opts.completions >= 1 && !opts.pilotMode
      && !opts.android && onMyDayTab(opts.search ?? "", opts.appTab);
  }
  return false;
}

const DISMISS_KEY = "trybeup_download_banner_dismissed_at";
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

type Platform = "ios-web" | "android-web" | "desktop" | "in-app";

function detectPlatform(): Platform {
  if (Capacitor.isNativePlatform()) return "in-app";
  // iPadOS 13+ reports as a touch Mac — lib/storeLinks handles that.
  const device = currentWebDevice();
  return device === "ios" ? "ios-web" : device === "android" ? "android-web" : "desktop";
}

function isRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export default function DownloadAppBanner() {
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [visible, setVisible] = useState(false);
  const location = useLocation();
  // The signed-in user, read (never fetched or observed) from useAuth's cache.
  const me = useCachedMe();
  // Re-read the completion counter when a step is done in this session, so
  // the banner can appear right after the first one, not only on next load.
  const [completions, setCompletions] = useState(() => readOptInState().completions);
  useEffect(() => {
    const onDone = () => setCompletions(readOptInState().completions);
    window.addEventListener(COMPLETION_EVENT, onDone);
    return () => window.removeEventListener(COMPLETION_EVENT, onDone);
  }, []);
  const [appTab, setAppTab] = useState<string | null>(getActiveAppTab);
  useEffect(() => {
    const onTab = () => setAppTab(getActiveAppTab());
    window.addEventListener(ACTIVE_TAB_EVENT, onTab);
    return () => window.removeEventListener(ACTIVE_TAB_EVENT, onTab);
  }, []);
  const onAllowedPath = bannerAllowed({
    path: location.pathname,
    signedIn: !!me?.id,
    completions,
    pilotMode: (me?.appMode ?? me?.app_mode) === "autopilot",
    search: location.search,
    appTab,
    android: platform === "android-web",
  });

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    // Don't show inside the Capacitor app — we ARE the app there.
    if (p === "in-app") return;
    // Honour the 30-day dismissal.
    if (isRecentlyDismissed()) return;
    // Small delay so the banner doesn't pop in over hero animations.
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* localStorage unavailable — banner just won't re-suppress next visit, fine */
    }
  };

  if (!visible || platform === "in-app" || platform === null) return null;
  // Path-aware: public marketing routes, or the signed-in app after a first
  // step (bannerAllowed). Never the logged-out Gateway.
  if (!onAllowedPath) return null;

  // iOS web — full-width sticky bottom banner (highest conversion intent)
  if (platform === "ios-web") {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-primary/40 bg-background/95 backdrop-blur px-4 py-3 shadow-2xl"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
        role="dialog"
        aria-label="Download TrybeUP for iPhone"
      >
        <div className="flex items-center gap-3 max-w-md mx-auto">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-md">
            <Apple className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground leading-tight">TrybeUP for iPhone</p>
            <p className="text-xs text-muted-foreground leading-tight mt-0.5">
              Faster, push notifications, offline ready.
            </p>
          </div>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 rounded-lg bg-foreground text-background text-sm font-semibold px-3 py-2 hover:bg-foreground/90 active:scale-[0.98] transition"
            data-testid="download-app-banner-cta"
          >
            Get
          </a>
          <button
            type="button"
            onClick={dismiss}
            className={cn(closeButtonClass, "flex-shrink-0")}
            aria-label="Dismiss download banner"
            data-testid="download-app-banner-dismiss"
          >
            <CloseIcon />
          </button>
        </div>
      </div>
    );
  }

  // Android web — full-width sticky bottom banner → Google Play
  if (platform === "android-web") {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-primary/40 bg-background/95 backdrop-blur px-4 py-3 shadow-2xl"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
        role="dialog"
        aria-label="Download TrybeUP for Android"
      >
        <div className="flex items-center gap-3 max-w-md mx-auto">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-md ring-1 ring-black/5">
            <GooglePlayIcon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground leading-tight">TrybeUP for Android</p>
            <p className="text-xs text-muted-foreground leading-tight mt-0.5">
              Faster, push notifications, offline ready.
            </p>
          </div>
          <a
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 rounded-lg bg-foreground text-background text-sm font-semibold px-3 py-2 hover:bg-foreground/90 active:scale-[0.98] transition"
            data-testid="download-app-banner-cta-android"
          >
            Get
          </a>
          <button
            type="button"
            onClick={dismiss}
            className={cn(closeButtonClass, "flex-shrink-0")}
            aria-label="Dismiss download banner"
            data-testid="download-app-banner-dismiss"
          >
            <CloseIcon />
          </button>
        </div>
      </div>
    );
  }

  // Desktop — small pill in bottom-right listing BOTH stores (visitor could have
  // either phone). Now that we're live on Google Play too.
  return (
    <div
      className="fixed bottom-4 right-4 z-50 max-w-xs rounded-xl border border-border bg-background/95 backdrop-blur shadow-xl px-3 py-2.5"
      role="dialog"
      aria-label="Get the TrybeUP app on iPhone or Android"
    >
      <div className="flex items-start gap-2.5">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-foreground leading-tight mb-1">
            Get the app on your phone
          </p>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-primary hover:underline"
            data-testid="download-app-banner-cta"
          >
            <AppleLogo className="w-3 h-3.5" /> Download on the App Store →
          </a>
          <a
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-primary hover:underline mt-1"
            data-testid="download-app-banner-cta-android"
          >
            <GooglePlayIcon className="w-3.5 h-3.5" /> Get it on Google Play →
          </a>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="flex-shrink-0 w-5 h-5 rounded-full hover:bg-muted/50 transition flex items-center justify-center -mt-0.5 -mr-0.5"
          aria-label="Dismiss download banner"
          data-testid="download-app-banner-dismiss"
        >
          <X className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
}
